import { useCallback, useState, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

import {
  HostContextPeekContext,
  type ContextSource,
  type HostContextPeek,
} from '../registry/internals/hostContextPeek';

type FeedContextLineProps = {
  // Off where the card keeps its reason under the title, so the card renders as it is
  enabled: boolean;
  children: ReactNode;
};

// The card's context as its first row: one line of the reason, with a count of the messages it
// answers. It starts closed, and opening it shows the full reason, then those messages in place. The line stands in the document's
// Reason, and the answered message moves into it (Reason.tsx, Message.tsx)
export const FeedContextLine = ({ enabled, children }: FeedContextLineProps) =>
  enabled ? <ContextLine>{children}</ContextLine> : children;

const ContextLine = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [sources, setSources] = useState<ContextSource[]>([]);
  const [sourceTarget, setSourceTarget] = useState<HTMLElement | null>(null);

  const registerSource = useCallback((source: ContextSource) => {
    setSources((shown) => [...shown, source]);
    return () => setSources((shown) => shown.filter((each) => each !== source));
  }, []);

  const count =
    sources.length > 0
      ? `${sources.length} ${sources.length === 1 ? 'email' : 'emails'}`
      : null;
  const Chevron = open ? Icons.chevronUp : Icons.chevronDown;
  // The document's resolved reason leads, so a card whose reason falls back to the message it answers keeps that
  // message. Without one, the line quotes the email it answers: its sender and first words
  const lineFor = (reasonText: string | null) => {
    const first = sources[0];
    const summary =
      reasonText ??
      (first ? `${first.author}: ${first.text.replace(/\s+/g, ' ')}` : null);
    if (!summary) return null;
    return (
      <div data-feed-context="">
        <div
          className={cn(
            'flex flex-col gap-[calc(10*var(--ac-u))] rounded-[calc(10*var(--ac-u))] border border-border-default bg-background px-[calc(14*var(--ac-u))] py-[calc(8*var(--ac-u))] dark:border-secondary',
            // The opened sources sit as far from the bottom edge as from the sides
            open && sources.length > 0 && 'pb-[calc(12*var(--ac-u))]'
          )}
        >
          <button
            type="button"
            aria-expanded={open}
            className="flex min-w-0 items-start gap-[calc(8*var(--ac-u))] text-left focus-ring"
            onClick={() => setOpen(!open)}
          >
            <span
              className={cn(
                'min-w-0 flex-1 text-[length:calc(12.5*var(--ac-u))] leading-[calc(18*var(--ac-u))]',
                // Closed, the line stays quiet; open, its full reason reads in the darker tone
                open ? 'text-foreground-subtle' : 'text-foreground-muted',
                (!open || !reasonText) && 'truncate'
              )}
            >
              {summary}
            </span>
            {count ? (
              <span className="shrink-0 rounded-full bg-secondary px-[calc(7*var(--ac-u))] text-[length:calc(11*var(--ac-u))] leading-[calc(18*var(--ac-u))] text-foreground-subtle">
                {count}
              </span>
            ) : null}
            <Chevron
              aria-hidden="true"
              className="mt-[calc(3*var(--ac-u))] size-[calc(12*var(--ac-u))] shrink-0 text-foreground-muted"
            />
          </button>
          <div
            ref={setSourceTarget}
            data-feed-context-sources=""
            hidden={!open || sources.length === 0}
            className="flex flex-col gap-[calc(10*var(--ac-u))] [&>*]:bg-white dark:[&>*]:bg-surface-active [&[hidden]]:hidden"
          />
        </div>
      </div>
    );
  };

  const host: HostContextPeek = {
    line: lineFor,
    sourceTarget,
    registerSource,
  };

  return (
    <HostContextPeekContext.Provider value={host}>
      {children}
    </HostContextPeekContext.Provider>
  );
};
